/* @jsx h */
import { defineComponent, h, ref, PropType, watch } from "vue";
import { useToast } from "@app/vue/providers/Toast";
import { useLocalization } from "@lemmonhq/uikit";
import type { DiscountFormState, DiscountType } from "@lemmonhq/types";
import { validateDiscountForm, discountToFormState } from "./utils";

export const DiscountTypeForm = defineComponent({
  name: "DiscountTypeForm",
  props: {
    mode: {
      type: String as PropType<"create" | "edit">,
      required: true,
    },
    discount: {
      type: Object as PropType<DiscountType | null>,
      default: null,
    },
    onSubmit: {
      type: Function as PropType<(data: DiscountFormState) => Promise<void>>,
      required: true,
    },
    onDelete: {
      type: Function as PropType<() => Promise<void>>,
      default: null,
    },
    onCancel: {
      type: Function as PropType<() => void>,
      required: true,
    },
  },
  setup(props) {
    const toast = useToast();
    const { t } = useLocalization();
    const submitting = ref(false);

    const defaultForm = (): DiscountFormState => ({
      title: "",
      type: "percentage",
      value: "",
      allow_changes: false,
    });

    const form = ref<DiscountFormState>(
      props.mode === "edit" && props.discount
        ? discountToFormState(props.discount)
        : defaultForm(),
    );

    watch(
      () => [props.mode, props.discount] as const,
      ([mode, discount]) => {
        form.value =
          mode === "edit" && discount
            ? discountToFormState(discount)
            : defaultForm();
      },
      { immediate: true },
    );

    const handleSubmit = async (event: Event) => {
      event.preventDefault();
      if (submitting.value) return;

      const error = validateDiscountForm(form.value, t);
      if (error) {
        toast.showToast(error, { type: "warning" });
        return;
      }

      try {
        submitting.value = true;
        await props.onSubmit(form.value);
      } finally {
        submitting.value = false;
      }
    };

    const handleDelete = async () => {
      if (!props.onDelete || submitting.value) return;

      try {
        submitting.value = true;
        await props.onDelete();
      } finally {
        submitting.value = false;
      }
    };

    return () => (
      <form class="flex h-full flex-col" onSubmit={handleSubmit}>
        <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          <div class="space-y-1">
            <p class="text-sm text-base-content/70">
              {props.mode === "create"
                ? t("settings.discount_types_page.form_create_description")
                : t("settings.discount_types_page.form_edit_description")}
            </p>
          </div>

          <div class="space-y-4">
            <label class="form-control w-full">
              <span class="label-text text-sm font-medium text-base-content/70">
                {t("settings.discount_types_page.label_discount_name")}
              </span>
              <input
                class="input input-bordered"
                placeholder={t(
                  "settings.discount_types_page.placeholder_discount_name",
                )}
                value={form.value.title}
                onInput={(event) => {
                  form.value.title = (event.target as HTMLInputElement).value;
                }}
              />
            </label>

            <div class="grid gap-4 md:grid-cols-2">
              <label class="form-control w-full">
                <span class="label-text text-sm font-medium text-base-content/70">
                  {t("settings.discount_types_page.label_type")}
                </span>
                <select
                  class="select select-bordered"
                  value={form.value.type}
                  onChange={(event) => {
                    form.value.type = (event.target as HTMLSelectElement)
                      .value as "fixed-amount" | "percentage";
                  }}
                >
                  <option value="percentage">
                    {t("settings.discount_types_page.type_percentage")}
                  </option>
                  <option value="fixed-amount">
                    {t("settings.discount_types_page.type_fixed_amount")}
                  </option>
                </select>
              </label>

              <label class="form-control w-full">
                <span class="label-text text-sm font-medium text-base-content/70">
                  {form.value.type === "percentage"
                    ? t("settings.discount_types_page.label_percentage")
                    : t("settings.discount_types_page.label_amount")}
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={form.value.type === "percentage" ? "100" : undefined}
                  class="input input-bordered"
                  placeholder={
                    form.value.type === "percentage"
                      ? t("settings.discount_types_page.placeholder_percentage")
                      : t("settings.discount_types_page.placeholder_amount")
                  }
                  value={form.value.value}
                  onInput={(event) => {
                    form.value.value = (event.target as HTMLInputElement).value;
                  }}
                />
              </label>
            </div>

            <label class="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                checked={form.value.allow_changes}
                onChange={(event) => {
                  form.value.allow_changes = (
                    event.target as HTMLInputElement
                  ).checked;
                }}
              />
              <span class="label-text text-sm">
                {t("settings.discount_types_page.checkbox_allow_adjust")}
              </span>
            </label>
          </div>
        </div>

        <div class="border-t bg-base-200/50 px-6 py-4 flex flex-wrap items-center justify-between gap-3 w-full">
          <div class="flex-1">
            {props.mode === "edit" ? (
              <button
                type="button"
                class="btn btn-ghost btn-sm"
                onClick={handleDelete}
                disabled={submitting.value}
              >
                {t("settings.discount_types_page.button_delete")}
              </button>
            ) : (
              <p class="text-sm text-base-content/60">
                {t("settings.discount_types_page.footer_note")}
              </p>
            )}
          </div>

          <div class="flex gap-2">
            <button
              type="submit"
              class="btn btn-primary btn-sm self-end"
              disabled={submitting.value}
            >
              {submitting.value
                ? t("settings.discount_types_page.button_saving")
                : props.mode === "create"
                  ? t("settings.discount_types_page.button_save_discount")
                  : t("settings.discount_types_page.button_save_changes")}
            </button>
          </div>
        </div>
      </form>
    );
  },
});
